"use client";

import { useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import ShadowCard from "@/components/common/ShadowCard";
import ContactInfoItem from "@/components/contact/ContactInfoItem";
import FormField from "@/components/contact/FormField";
import Button from "@/components/ui/Button";
import { MdOutlineLocationOn, MdOutlineMail } from "react-icons/md";
import { IoCallOutline } from "react-icons/io5";
import { validateUSPhoneNumber } from "@/utils/validation";

export default function ContactUsContent() {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    services: "",
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear phone error when user types
    if (name === "phone") {
      setPhoneError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    if (!executeRecaptcha) {
      setError("reCAPTCHA not loaded. Please try again.");
      setLoading(false);
      return;
    }

    // Validate phone number
    if (!validateUSPhoneNumber(formData.phone)) {
      setPhoneError("Please enter a valid US phone number (10 digits)");
      setLoading(false);
      return;
    }

    try {
      // Generate reCAPTCHA token
      const recaptchaToken = await executeRecaptcha("contact_form");

      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          sourceUrl: window.location.href,
          recaptchaToken,
        }),
      });

      if (response.ok) {
        setSuccess(true);
        setFormData({ name: "", email: "", phone: "", services: "" });
        setTimeout(() => setSuccess(false), 5000);
      } else {
        const data = await response.json();
        setError(data.error || "Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Failed to submit. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="bg-brand-black min-h-screen">
      {/* Main Section */}
      <section className="relative px-5 py-[100px] md:px-12 lg:px-[100px] lg:py-24">
        <div className="relative z-10 flex flex-col gap-10 lg:flex-row lg:items-center lg:justify-between lg:gap-16">
          {/* Left Content */}
          <div className="flex-1 text-center lg:max-w-2xl lg:text-left">
            {/* Headline */}
            <h1 className="font-raleway mb-[100px] text-[2rem] leading-[1.3] font-extrabold tracking-wide text-white uppercase md:text-4xl lg:mb-[40px] lg:text-5xl">
              <span className="block lg:inline">Get In Touch</span>{" "}
              <span className="from-brand-red to-brand-dark-red block bg-linear-to-br bg-clip-text text-transparent lg:inline">
                With Us!
              </span>
            </h1>

            {/* Subheadline */}
            <h2 className="font-raleway mb-5 text-xl font-bold text-white lg:mb-5">
              Ready to increase your business&apos; revenue by 25%-30% at NO
              cost?
            </h2>

            {/* Description */}
            <p className="font-raleway mb-10 text-start text-base leading-[1.3] text-white/80 lg:mb-[40px]">
              Get in touch with our team today for all of your COAM machine
              needs. For questions, concerns, a repair or maintenance service,
              reach out and one of our technicians will get back to you as soon
              as possible
            </p>

            {/* Contact Info */}
            <div className="mb-10 flex flex-col gap-5 lg:mb-0 lg:flex-row lg:flex-wrap lg:items-center lg:gap-6">
              <ContactInfoItem icon={MdOutlineLocationOn}>
                <a
                  href="https://maps.app.goo.gl/a29ubXwXJko2k8BHA"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <p>345 Oglethorpe St Macon</p>
                  <p>GA 31201</p>
                </a>
              </ContactInfoItem>

              <ContactInfoItem icon={IoCallOutline}>
                844-792-7280
              </ContactInfoItem>

              <ContactInfoItem icon={MdOutlineMail}>
                info@halcyonamusements.com
              </ContactInfoItem>
            </div>
          </div>

          {/* Right Content */}
          <div className="w-full lg:w-auto lg:min-w-[500px]">
            <ShadowCard className="from-brand-red to-brand-dark-red bg-linear-to-br p-5">
              <h3 className="font-raleway mb-10 text-center text-[2rem] font-bold text-white">
                Fill out the form!
              </h3>

              <div className="relative">
                {/* Success Message Overlay */}
                {success && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                    <h3 className="font-raleway mb-2 text-2xl font-bold text-white">
                      Message Sent!
                    </h3>
                    <p className="font-raleway text-white/90">
                      We will get back to you shortly.
                    </p>
                  </div>
                )}

                {/* Form - Hidden but takes space when success is true */}
                <form
                  onSubmit={handleSubmit}
                  className={`flex flex-col gap-5 transition-opacity duration-300 ${
                    success ? "invisible opacity-0" : "visible opacity-100"
                  }`}
                >
                  <FormField
                    label="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                  />

                  <FormField
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter your email address"
                  />

                  <FormField
                    label="Phone number"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Enter your phone number"
                  />
                  {phoneError && (
                    <p className="mt-1 text-sm font-semibold text-red-200">
                      {phoneError}
                    </p>
                  )}

                  <FormField
                    label="Services"
                    name="services"
                    value={formData.services}
                    onChange={handleChange}
                    required
                    placeholder="Enter services you are interested in"
                  />

                  {error && (
                    <p className="rounded bg-red-500/20 p-3 text-center text-sm font-semibold text-red-200">
                      {error}
                    </p>
                  )}

                  {/* Submit Button */}
                  <div className="mt-5 flex justify-center">
                    <Button variant="white" disabled={loading}>
                      {loading ? "Sending..." : "Submit"}
                    </Button>
                  </div>
                </form>
              </div>
            </ShadowCard>
          </div>
        </div>
      </section>
    </main>
  );
}
